import React from 'react';
import {
  UserPlus,
  MapPin,
  FlaskConical,
  CheckSquare,
  Zap,
  PhoneCall,
  ArrowLeftRight,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/shared/utils/utils';

interface QuickActionsBarProps {
  onOpenModal: (actionType: 'lead' | 'visit' | 'sample' | 'task' | 'followup') => void;
  onOpenForex?: () => void;
}

interface QuickAction {
  label: string;
  icon: LucideIcon;
  /** Icon tint only — the buttons themselves stay neutral. */
  tint: string;
  onClick: () => void;
  primary?: boolean;
}

/**
 * One primary action and a row of neutral secondary ones. Each button used to
 * carry its own full colour scheme (eight different tints side by side), which
 * read as a toy rather than a work tool; colour now marks only the icon.
 */
export const QuickActionsBar: React.FC<QuickActionsBarProps> = ({ onOpenModal, onOpenForex }) => {
  const actions: QuickAction[] = [
    // No Create Quotation / Proforma / Invoice shortcuts here (client, Sep-2026):
    // documents are raised from a Sales Pipeline record, so every one is tied
    // to its enquiry. A shortcut here let people create them with no record.
    { label: 'Add Lead', icon: UserPlus, tint: 'text-on-primary', onClick: () => onOpenModal('lead'), primary: true },
    { label: 'Add Sample', icon: FlaskConical, tint: 'text-warning-text', onClick: () => onOpenModal('sample') },
    { label: 'Log Visit', icon: MapPin, tint: 'text-success-text', onClick: () => onOpenModal('visit') },
    { label: 'Schedule Call', icon: PhoneCall, tint: 'text-danger-text', onClick: () => onOpenModal('followup') },
    { label: 'Add Task', icon: CheckSquare, tint: 'text-on-surface-variant', onClick: () => onOpenModal('task') },
    ...(onOpenForex ? [{ label: 'Forex Desk', icon: ArrowLeftRight, tint: 'text-on-surface-variant', onClick: onOpenForex }] : []),
  ];

  return (
    <div className="mb-5 flex flex-col gap-3 rounded-card border border-border bg-card px-4 py-3 shadow-surface-1 lg:flex-row lg:items-center">
      <div className="flex shrink-0 items-center gap-2 lg:w-40">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Zap className="h-4 w-4" aria-hidden />
        </span>
        <span className="text-label-caps uppercase text-on-surface-variant">Quick Actions</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {actions.map(({ label, icon: Icon, tint, onClick, primary }) => (
          <button
            key={label}
            type="button"
            onClick={onClick}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-body-sm font-semibold transition-colors duration-150 active:translate-y-px',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              primary
                ? 'bg-primary text-on-primary shadow-surface-1 hover:bg-primary/90'
                : 'border border-border bg-surface text-on-surface hover:border-primary/30 hover:bg-surface-variant',
            )}
          >
            <Icon className={cn('h-3.5 w-3.5', tint)} aria-hidden />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};
